import com.fireavert.common.TableColumn
import com.fireavert.fire_risk.client.presentation.RiskTableController
import com.fireavert.fire_risk.client.presentation.RiskTableViewModel
import com.fireavert.fire_risk.data_models.RiskLevel
import com.fireavert.fire_risk.data_models.RiskTableColumns
import com.fireavert.fire_risk.data_models.TenantFireRiskDataModel
import com.fireavert.logging.Logger
import com.fireavert.menu.menu_navigation.client.RoutePaths
import com.fireavert.properties.shared.data_models.Property
import com.fireavert.react.pages.my_fire_risk.FireRiskLabelComponent
import com.fireavert.react.pages.my_fire_risk.RiskTableKey
import com.fireavert.react.pages.reports.styles.ReportPageStyles
import com.fireavert.react.shared.PageHeader
import com.fireavert.react.shared.components.tables.SortBar
import com.fireavert.react.shared.components.tables.mapToControlColumns
import com.fireavert.react.shared.components.tables.mapToStateColumns
import com.fireavert.react.shared.styles.MobileStyles.MOBILE_VIEW_WIDTH
import com.fireavert.react.shared.styles.StandardizedStyles
import com.fireavert.react.shared.styles.TableStyles
import com.fireavert.reports_page.shared.data_models.EventTypes
import com.fireavert.utilities.getKoinInstance
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import mui.material.Skeleton
import mui.material.SkeletonVariant
import mui.system.sx
import mui.system.useMediaQuery
import react.FC
import react.Props
import react.dom.html.ReactHTML
import react.router.useNavigate
import react.useEffectOnceWithCleanup
import react.useState
import web.cssom.pct
import web.cssom.px

val MyFireRiskSection = FC<Props> {

    val logger = getKoinInstance<Logger>()
    val viewModel = getKoinInstance<RiskTableViewModel>()
    val controller = getKoinInstance<RiskTableController>()

    val isMobile = useMediaQuery(MOBILE_VIEW_WIDTH)

    var reloadJob: Job? = null
    var managementCompaniesWithProperties: Map<String, List<Property>> by useState(emptyMap())
    var loading: Boolean by useState(false)

    var propertyId: Int by useState(-1)
    var propertyMap: Map<String, Int?> by useState(mapOf("None" to -1, "All" to null))
    var averageEventsPerMonth: Double by useState(0.0)
    var allTimeAverageEventValue: Double by useState(0.0)

    var highRiskTenantData: List<TenantFireRiskDataModel> by useState(emptyList())
    var loadingHighRiskTenants: Boolean by useState(true)

    val navigate = useNavigate()
    var expandedRows: Set<String> by useState(emptySet()) // Track expanded rows by management company
    val (searchValue, setSearchValue) = useState("")
    var selectedEvents by useState(
        listOf(
            EventTypes.SMOKE.toString()
        )
    )

    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            loading = viewState.loading
            highRiskTenantData = viewState.fireRiskTenantData
            propertyId = viewState.propertyId ?: -1
            propertyMap = viewState.propertyMap
            loadingHighRiskTenants = viewState.loadingHighRiskTenants

            averageEventsPerMonth = viewState.averageEventsPerMonth
            allTimeAverageEventValue = viewState.allTimeAverageEventValue
            // Auto-expand management companies when search is active and companies list changes
            if (searchValue.isNotEmpty() && managementCompaniesWithProperties.isNotEmpty()) {
                expandedRows = managementCompaniesWithProperties.keys.toSet()
            }
        }.launchIn(viewModel.scope)
        val onLoadJob = viewModel.scope.launch {
            logger.i("useEffectOnceWithCleanup: loading data")
            controller.onLoad()
        }

        onCleanup {
            viewStateJob.cancel()
            onLoadJob.cancel()
            viewStateJob.cancel()
        }
    }

    fun updateData(newPropertyId: Int?, eventTypeValue: String) {
        reloadJob?.cancel()
        reloadJob = viewModel.scope.launch {
            controller.updateData(newPropertyId, eventTypeValue)
        }
    }

    var stateColumns: Array<TableColumn> by useState(
        arrayOf(
            TableColumn(
                text = RiskTableColumns.RISK_LEVEL.columnName,
                sortOrder = TableColumn.SortOrder.HighToLow,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = RiskTableColumns.PROPERTY_NAME.columnName,
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = RiskTableColumns.UNIT.columnName,
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = RiskTableColumns.TAKE_ACTION.columnName,
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
        )
    )

    PageHeader {
        pageTitle = "My Fire Risk"
        deleteMode = false
        linkCalls = emptyList()
        rightContent = RiskTableKey
    }

    ReactHTML.div {
        className = StandardizedStyles.HeaderH2
        +"High Risk Tenants"
    }
    ReactHTML.div {
        className = TableStyles.tableContainerNew
        ReactHTML.div {
            className = ReportPageStyles.highRiskTenantTable
            if (!loadingHighRiskTenants) {
                if (highRiskTenantData.isEmpty()) {
                    logger.i("highRiskTenantsRecord is empty")
                    ReactHTML.div {
                        className = ReportPageStyles.noDataMessage
                        +"No Data Available Yet"
                    }
                } else {
                    ReactHTML.div {
                        className = TableStyles.tableNew
                        SortBar {
                            this.columns = mapToControlColumns(stateColumns) // TODO: add mobile columns
                            includeLoading = false
                            isLoading = loading
                            columnsChanged = {
                                stateColumns = mapToStateColumns(it)
                            }
                            this.isNewView = true
                        }
                        val sortedHighRiskTenantData =
                            controller.sortTrackingRecordsByColumn(
                                stateColumns,
                                highRiskTenantData,
                            )
                        for (highRiskTenant in sortedHighRiskTenantData) {
                            val riskLevel = RiskLevel.fromNumber(highRiskTenant.riskRating) ?: RiskLevel.ONE
                            ReactHTML.div {
                                    onClick = {
                                        navigate("${RoutePaths.properties}/$propertyId/units/${highRiskTenant.unitId}")
                                    }
                                ReactHTML.div {
                                    className = TableStyles.tableCell
                                    FireRiskLabelComponent {
                                        this.riskLevel = riskLevel
                                    }
                                }
                                className = ReportPageStyles.reportHighRiskTenantRow
                                ReactHTML.div {
                                    className = TableStyles.tableCell
                                    +highRiskTenant.propertyName
                                }
                                ReactHTML.div {
                                    className = TableStyles.tableCell
                                    +highRiskTenant.unitNumber
                                }
                                ReactHTML.div {
                                    className = TableStyles.tableCell
                                    +"Lower the Risk"
                                }

                            }
                        }
                    }
                }
            } else {
                Skeleton {
                    sx {
                        borderRadius = 10.px
                    }
                    variant = SkeletonVariant.rectangular
                    width = 100.pct
                    height = 350.px
                }
            }
        }
    }
}
